import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { X, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';

const tourSteps = [
  {
    id: 'welcome',
    title: 'Welcome to Trinity Dental Arts! 👋',
    description: 'Let us show you around our website to help you find exactly what you need.',
    target: null,
    position: 'center'
  },
  {
    id: 'services',
    title: 'Explore Our Services',
    description: 'Click here to discover our comprehensive dental services including implants, cosmetic dentistry, and more.',
    target: '[data-tour="services"]',
    position: 'bottom'
  },
  {
    id: 'book',
    title: 'Book Your Appointment',
    description: 'Ready to schedule? Click here for instant online booking - no waiting!',
    target: '[data-tour="book-appointment"]',
    position: 'bottom'
  },
  {
    id: 'treatment-finder',
    title: 'Not Sure What You Need?',
    description: 'Try our AI-powered treatment finder to get personalized recommendations.',
    target: '[data-tour="treatment-finder"]',
    position: 'top'
  },
  {
    id: 'contact',
    title: 'Get In Touch',
    description: 'Have questions? Call us at (727) 228-6846 or use our contact form.',
    target: '[data-tour="contact"]',
    position: 'top'
  }
];

export default function OnboardingTour() {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  useEffect(() => {
    // Check if user has seen the tour
    const hasSeenTour = localStorage.getItem('hasSeenOnboardingTour');
    const isFirstVisit = !localStorage.getItem('visitCount');
    
    if (!hasSeenTour && isFirstVisit) {
      // Show tour after 2 seconds on first visit
      const timer = setTimeout(() => setIsActive(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (!isActive) return;

    const updateTargetPosition = () => {
      const step = tourSteps[currentStep];
      if (step.target) {
        const element = document.querySelector(step.target);
        if (element) {
          const rect = element.getBoundingClientRect();
          setTargetRect(rect);
        }
      } else {
        setTargetRect(null);
      }
    };

    updateTargetPosition();
    window.addEventListener('resize', updateTargetPosition);
    window.addEventListener('scroll', updateTargetPosition);

    return () => {
      window.removeEventListener('resize', updateTargetPosition);
      window.removeEventListener('scroll', updateTargetPosition);
    };
  }, [currentStep, isActive]);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('hasSeenOnboardingTour', 'true');
    setIsActive(false);
  };

  const handleSkip = () => {
    localStorage.setItem('hasSeenOnboardingTour', 'true');
    setIsActive(false);
  };

  const getTooltipPosition = () => {
    if (!targetRect) {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
    }

    const step = tourSteps[currentStep];
    const padding = 20;

    switch (step.position) {
      case 'bottom':
        return {
          top: `${targetRect.bottom + padding}px`,
          left: `${targetRect.left + targetRect.width / 2}px`,
          transform: 'translateX(-50%)'
        };
      case 'top':
        return {
          top: `${targetRect.top - padding}px`,
          left: `${targetRect.left + targetRect.width / 2}px`,
          transform: 'translate(-50%, -100%)'
        };
      default:
        return {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        };
    }
  };

  if (!isActive) return null;

  const step = tourSteps[currentStep];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999]"
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleSkip} />

        {/* Spotlight */}
        {targetRect && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute pointer-events-none"
            style={{
              top: targetRect.top - 8,
              left: targetRect.left - 8,
              width: targetRect.width + 16,
              height: targetRect.height + 16,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
              borderRadius: '8px',
              border: '2px solid #3b82f6',
              zIndex: 9998
            }}
          />
        )}

        {/* Tooltip */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="absolute bg-white rounded-xl shadow-2xl p-6 max-w-md"
          style={getTooltipPosition()}
        >
          <button
            onClick={handleSkip}
            aria-label="Close tour"
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>

          <div className="pr-8">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-5 w-5 text-blue-600" />
              <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
            </div>
            <p className="text-gray-600 mb-6">{step.description}</p>

            <div className="flex items-center justify-between">
              <div className="flex gap-1">
                {tourSteps.map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-2 w-2 rounded-full transition-colors ${
                      idx === currentStep ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              <div className="flex gap-2">
                {currentStep > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handlePrevious}
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={handleNext}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {currentStep === tourSteps.length - 1 ? 'Get Started' : 'Next'}
                  {currentStep < tourSteps.length - 1 && <ArrowRight className="h-4 w-4 ml-1" />}
                </Button>
              </div>
            </div>

            <button
              onClick={handleSkip}
              className="text-sm text-gray-500 hover:text-gray-700 mt-4 block mx-auto"
            >
              Skip tour
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}